/*
 * Copyright (c) 2024 SGS Europe. All rights reserved.
 *
 * This program is the CONFIDENTIAL and PROPRIETARY property of SGS Europe.
 * Any unauthorized use, reproduction, or transfer of this program is strictly
 * prohibited.
 *
 * File Name: GreenlightTextCompare_FullDocumentSelection.js
 *
 * Description: The class containing the functionality required by the Greenlight for text compare auto auto selection.
 * 
 */

// The text tools object.
var g_oTextCompare_FullDocumentSelection = {};

(function () {
    "use strict";

    /**
     * Load the text compare auto auto selection for the page.
     */
    function TextCompareFullDocumentSelection() {
        this.SHORT_TIMEOUT = 25;
        this.LONG_PAGE_CHANGED_TIMEOUT = 1150;
    }

    /**
     * Start the auto auto selection process.
     * @param {object} oCompareCanvas - The compare canvas.
     * @param {object} oArtworkCanvas - The artwork canvas.
     * @param {boolean} bCompareCanvas - If the compare canvas is being used.
     * @param {number} iMinWords - The minimum number of words to match.
     */
    TextCompareFullDocumentSelection.prototype.processFullDocumentSelection = function (oCompareCanvas, oArtworkCanvas, bCompareCanvas, iMinWords) {

        // Lock the screen with the progress bar.
        g_oProgressBar.addProgressBar("Preparing...", true);

        // Begin calculating within a timeout to allow the progress bar message to be shown.
        setTimeout(function () {
            // Keep track of the original text comparison accuracy.
            var iOldTextComparisonAccuracy = g_oSettings.g_oUserSettings.TextComparisonAccuracy;
            g_oSettings.g_oUserSettings.TextComparisonAccuracy = 1;

            // Extract sequential pages for artwork.
            var oArtWorkSequentialPages = g_oTextCompare_AutoSelection.extractSequentialPages(oArtworkCanvas, oCompareCanvas, null);

            // Extract sequential pages for compare which we match words on.
            var oCompareSequentialPages = g_oTextCompare_AutoSelection.extractSequentialPages(oCompareCanvas, oArtworkCanvas, null);

            // Get the exact matches.
            g_oTextCompare_FullDocumentSelection.getExactWordMatches(oArtWorkSequentialPages, oArtworkCanvas, oCompareCanvas, oCompareSequentialPages, bCompareCanvas, iMinWords, function(exactMatches) {
                // Process the exact matches.
                g_oTextCompare_FullDocumentSelection.processExactMatches(exactMatches, oCompareCanvas, oArtworkCanvas, oArtWorkSequentialPages, bCompareCanvas, oCompareSequentialPages);

                // Reset the users text comparison accuracy.
                g_oSettings.g_oUserSettings.TextComparisonAccuracy = iOldTextComparisonAccuracy;
            });

        }, 100);
    };

    /**
     * Processes and selects the exact matches found during the auto auto selection process.
     * @param {Array} exactMatches - The array of exact matches.
     * @param {object} oCompareCanvas - The compare canvas.
     * @param {object} oArtworkCanvas - The artwork canvas.
     * @param {Array} oArtWorkSequentialPages - The sequential pages for the artwork.
     * @param {boolean} bCompareCanvas - If the compare canvas is being used.
     * @param {Array} oSequentialPagesForMatching - The sequential pages for matching.
     */
    TextCompareFullDocumentSelection.prototype.processExactMatches = function (exactMatches, oCompareCanvas, oArtworkCanvas, oArtWorkSequentialPages, bCompareCanvas, oSequentialPagesForMatching) {
        var iTimeoutAmount = g_oTextCompare_FullDocumentSelection.SHORT_TIMEOUT;

        function processPhrase(phrase, oCompareCanvas, oArtworkCanvas, oPages, bCompareCanvas, index, bInitialRun) {
            setTimeout(function () {

                // Update the progress bar, accounting for the fact that we run through each canvas one after the other.
                var percentageIndex = bInitialRun ? index + 1 : exactMatches.length + index + 1;
                g_oTextCompare_FullDocumentSelection.updateProgress(percentageIndex, exactMatches.length * 2, "Applying Matches...", true);

                var iOldTextComparisonAccuracy = g_oSettings.g_oUserSettings.TextComparisonAccuracy;
                g_oSettings.g_oUserSettings.TextComparisonAccuracy = 1;

                if (bInitialRun && index > 0) {
                    var textSelectionGroups = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);
                    // The auto-selected group will always be the highest group order.
                    var selectedGroup = g_oTextCompare_AutoSelection.getHighestGroupOrder(textSelectionGroups);
                    selectedGroup.autoSelectLinkId = g_oTextCompare_AutoSelection.g_iAutoSelectLinkId++;
                    exactMatches[index - 1].group = selectedGroup;
                }
                // Clear any existing indexes.
                g_oTextCompare_AutoSelection.selectedIndexes = [];

                var bAutoSelectionFound = g_oTextCompare_AutoSelection.startDirectMatchSelectionForPages(phrase.text, oArtworkCanvas, oPages[0], false);
                if (!bAutoSelectionFound && g_oTextCompare_AutoSelection.selectedIndexes.length === 0) {
                    bAutoSelectionFound = g_oTextCompare_AutoSelection.startDirectMatchSelectionForPages(phrase.text, oArtworkCanvas, oPages[0], true);
                }

                // Check if the page will change.
                if (g_oTextCompare_AutoSelection.selectedIndexes.length > 1 || g_oTextCompare_AutoSelection.selectedIndexes[0].pageNumber !== oArtworkCanvas.g_iCurrentPage) {
                    iTimeoutAmount = g_oTextCompare_FullDocumentSelection.LONG_PAGE_CHANGED_TIMEOUT * g_oTextCompare_AutoSelection.selectedIndexes.length;
                }
                else {
                    iTimeoutAmount = g_oTextCompare_FullDocumentSelection.SHORT_TIMEOUT;
                }

                if (bInitialRun) {
                    g_oTextCompare_AutoSelection.handleTextSelection(!bCompareCanvas, oArtworkCanvas, null, false, false);
                }
                else {
                    g_oTextCompare_AutoSelection.handleTextSelection(!bCompareCanvas, oArtworkCanvas, phrase.group, true, false);
                }

                g_oTextCompare.renderTextCompareAnnotations();

                if (index < exactMatches.length - 1) {
                    g_oSettings.g_oUserSettings.TextComparisonAccuracy = iOldTextComparisonAccuracy;
                    processPhrase(exactMatches[index + 1], oCompareCanvas, oArtworkCanvas, oPages, bCompareCanvas, index + 1, bInitialRun);
                }
                else if (index === exactMatches.length - 1) {
                    if (bInitialRun) {
                        setTimeout(function () {
                            var textSelectionGroups = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);
                            // The auto-selected group will always be the highest group order.
                            var selectedGroup = g_oTextCompare_AutoSelection.getHighestGroupOrder(textSelectionGroups);
                            selectedGroup.autoSelectLinkId = g_oTextCompare_AutoSelection.g_iAutoSelectLinkId++;

                            exactMatches[index].group = selectedGroup;

                            exactMatches.sort(function (a, b) {
                                if (a.selectedIndexes[0].pageNumber === b.selectedIndexes[0].pageNumber) {
                                    return a.selectedIndexes.length - b.selectedIndexes.length;
                                }
                                return a.selectedIndexes[0].pageNumber - b.selectedIndexes[0].pageNumber;
                            });

                            iTimeoutAmount = g_oTextCompare_FullDocumentSelection.SHORT_TIMEOUT;
                            g_oSettings.g_oUserSettings.TextComparisonAccuracy = iOldTextComparisonAccuracy;
                            processPhrase(exactMatches[0], oArtworkCanvas, oCompareCanvas, oSequentialPagesForMatching, !bCompareCanvas, 0, false);
                        }, iTimeoutAmount);
                    }
                    else {
                        g_oProgressBar.removeProgressBar();
                        g_oTextCompare_AutoSelection.selectedIndexes = [];
                        g_oGreenlightMessages.displaySuccessBubble(exactMatches.length + " " + g_oTextCompare_FullDocumentSelection.c_szMatchesFoundAndSelected, 100);
                        g_oSettings.g_oUserSettings.TextComparisonAccuracy = iOldTextComparisonAccuracy;
                    }
                }
            }, iTimeoutAmount);
        }

        // If we have matches, process these, otherwise remove the progress bar.
        if (exactMatches.length > 0) {
            processPhrase(exactMatches[0], oCompareCanvas, oArtworkCanvas, oArtWorkSequentialPages, bCompareCanvas, 0, true);
        }
        else {
            g_oGreenlightMessages.displayWarningBubble(g_oTextCompare_FullDocumentSelection.c_szNoMatchesFound, 100);
            g_oProgressBar.removeProgressBar();
		}
    };

    /**
     * Retrieves the currently selected phrases from the text comparison groups.
     * @param {boolean} bCompareCanvas - Indicates if the compare canvas is being used.
     * @returns {Array} An array of already selected phrases.
     */
    TextCompareFullDocumentSelection.prototype.getSelectedPhrases = function (bCompareCanvas) {
        var oAlreadySelectedPhrases = [];
        var oGroups = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

        for (var i = 0; i < oGroups.length; i++) {
            var group = oGroups[i];
            var combinedSelectionText = "";
            for (var j = 0; j < group.g_oTextSelections.length; j++) {
                var selection = group.g_oTextSelections[j];
                combinedSelectionText += selection.g_oSelectionWordsText + " ";
            }
            oAlreadySelectedPhrases.push(combinedSelectionText.trim());
        }

        return oAlreadySelectedPhrases;
    };

    /**
     * Updates the progress bar with a delay.
     * @param {number} index - The current index of the word being processed.
     * @param {number} totalWords - The total number of words to process.
     */
    TextCompareFullDocumentSelection.prototype.updateProgressWithDelay = function (index, totalWords) {
        setTimeout(function () {
            g_oTextCompare_FullDocumentSelection.updateProgress(index + 1, totalWords, "Calculating Matches...", false);
        }, 0);
    };

    /**
     * Retrieves exact matches.
     * @param {Array} oArtWorkSequentialPages - The sequential pages for the artwork.
     * @param {object} oArtworkCanvas - The artwork canvas.
     * @param {object} oCompareCanvas - The compare canvas.
     * @param {Array} oSequentialPagesForMatching - The sequential pages for matching.
     * @param {boolean} bCompareCanvas - Indicates if the compare canvas is being used.
     * @param {number} iMinWords - The minimum number of words to match.
     * @returns {Array} An array of found phrases with their selected indexes.
     */
    TextCompareFullDocumentSelection.prototype.getExactWordMatches = function (oArtWorkSequentialPages, oArtworkCanvas, oCompareCanvas, oSequentialPagesForMatching, bCompareCanvas, iMinWords, callback) {
        // Get the full text and list of words from the first page for the artwork document.
        var szFullTextAW = g_oTextCompare_AutoSelection.getPagesFullText(oArtWorkSequentialPages[0], oArtworkCanvas);
        var oWords = szFullTextAW.split(" ");

        var oAlreadySelectedPhrases = g_oTextCompare_FullDocumentSelection.getSelectedPhrases(bCompareCanvas);

        // Get the full text and list of words from the first page for the compare document.
        var szFullTextCompare = g_oTextCompare_AutoSelection.getPagesFullText(oSequentialPagesForMatching[0], oCompareCanvas);
        var oWordsCompare = szFullTextCompare.split(" ");

        // Find the longest word length of the compare document.
        var iHighestWordLength = 0;
        for (var i = 0; i < oWordsCompare.length; i++) {
            if (oWordsCompare[i].length > iHighestWordLength) {
                iHighestWordLength = oWordsCompare[i].length;
            }
        }

        var szCurrentPhrase = "";
        var foundPhrases = [];
        var previousSelectedIndexes = [];
        var bSuccess = false;
        var iLastTimeout = 0;
        var iAddedWords = 0;
        var iFailedWords = 1;

        function processWord(i) {
            if (i >= oWords.length) {
                // Check if the last phrase should be pushed to foundPhrases
                if (szCurrentPhrase.split(" ").length >= iMinWords && szCurrentPhrase !== "" && previousSelectedIndexes.length > 0 && oAlreadySelectedPhrases.indexOf(szCurrentPhrase) === -1) {
                    foundPhrases.push({ text: szCurrentPhrase, selectedIndexes: previousSelectedIndexes });
                }

                // Remove duplicates based on the text property
                var uniqueFoundPhrases = [];
                var seenTexts = new Set();

                for (var j = 0; j < foundPhrases.length; j++) {
                    if (!seenTexts.has(foundPhrases[j].text)) {
                        uniqueFoundPhrases.push(foundPhrases[j]);
                        seenTexts.add(foundPhrases[j].text);
                    }
                }

                foundPhrases = uniqueFoundPhrases;

                // Call the callback function with the found phrases
                if (callback) {
                    callback(foundPhrases);
                }
                return;
            }

            g_oTextCompare_FullDocumentSelection.updateProgressWithDelay(i + 1, oWords.length);

            var oWord = oWords[i];
            szCurrentPhrase !== "" ? szCurrentPhrase += " " + oWord : szCurrentPhrase = oWord;

            if (szCurrentPhrase.split(" ").length < iMinWords) {
                iAddedWords = 0;
                iFailedWords = 1;
                // Call the timeout every 25 words or more to allow the DOM to update.
                if (i - iLastTimeout > 25) {
                    iLastTimeout = i;
                    setTimeout(function() { processWord(i + 1); }, 0);
                } else {
                    processWord(i + 1);
                }
                return;
            }

            iAddedWords++;

            var bAutoSelectionFound = g_oTextCompare_AutoSelection.startDirectMatchSelectionForPages(szCurrentPhrase, oCompareCanvas, oSequentialPagesForMatching[0], false);
            if (!bAutoSelectionFound && g_oTextCompare_AutoSelection.selectedIndexes.length === 0) {
                bAutoSelectionFound = g_oTextCompare_AutoSelection.startDirectMatchSelectionForPages(szCurrentPhrase, oCompareCanvas, oSequentialPagesForMatching[0], true);
            }

            if (g_oTextCompare_AutoSelection.selectedIndexes.length > 0 && i !== oWords.length - 1) {
                iFailedWords = 1;
                iAddedWords = 0;
                bSuccess = true;
                previousSelectedIndexes = JSON.parse(JSON.stringify(g_oTextCompare_AutoSelection.selectedIndexes));
                g_oTextCompare_AutoSelection.selectedIndexes = [];
                processWord(i + 1);
            }
            else if ((szCurrentPhrase.replace(/\s/g, '').length < iHighestWordLength || iAddedWords < 5) && (!bSuccess || iAddedWords < 5) && i !== oWords.length - 1) {
                if (bSuccess) {
                    // Count the extra failed words
                    iFailedWords++;
				}
                processWord(i + 1);
            } else {
                var iszCurrentWordLength = szCurrentPhrase.split(" ").length;
                var bHasFoundSomethingAtEnd = g_oTextCompare_AutoSelection.selectedIndexes.length > 0
                    && i === oWords.length - 1
                    && iszCurrentWordLength >= iMinWords;

                if (g_oTextCompare_AutoSelection.selectedIndexes.length === 0 && bSuccess) {
                    // Remove any extra failed words
                    var words = szCurrentPhrase.split(" ");
                    szCurrentPhrase = words.slice(0, -iFailedWords).join(" ");
                    i -= iFailedWords - 1;

                } else if (g_oTextCompare_AutoSelection.selectedIndexes.length > 0) {
                    previousSelectedIndexes = JSON.parse(JSON.stringify(g_oTextCompare_AutoSelection.selectedIndexes));
                    g_oTextCompare_AutoSelection.selectedIndexes = [];
                }

                if (szCurrentPhrase.split(" ").length >= iMinWords && bSuccess) {
                    if (foundPhrases.indexOf(szCurrentPhrase) === -1 && oAlreadySelectedPhrases.indexOf(szCurrentPhrase) === -1) {
                        foundPhrases.push({ text: szCurrentPhrase, selectedIndexes: previousSelectedIndexes });
                    }
                    bSuccess = false;
                    previousSelectedIndexes = [];
                    szCurrentPhrase = "";
                    processWord(i);
                } // Something was found in the last index that hasnt already been added
				else if (bHasFoundSomethingAtEnd &&
                    foundPhrases.indexOf(szCurrentPhrase) === -1 &&
                    oAlreadySelectedPhrases.indexOf(szCurrentPhrase) === -1) {
                    foundPhrases.push({ text: szCurrentPhrase, selectedIndexes: previousSelectedIndexes });
                    bSuccess = false;
                    previousSelectedIndexes = [];
                    szCurrentPhrase = "";
                    processWord(oWords.length);
				}
                else {
                    bSuccess = false;
                    previousSelectedIndexes = [];
                    szCurrentPhrase = "";
                    processWord(i - iszCurrentWordLength + 2);
                }
            }
        }

        processWord(0);
    };

    /**
     * Updates the progress bar during the auto auto selection process.
     * @param {number} number - The current progress number.
     * @param {number} count - The total count for the progress.
     * @param {string} message - The message to display on the progress bar.
     * @param {boolean} bPartTwo - Indicates if this is the second part of the process.
     */
    TextCompareFullDocumentSelection.prototype.updateProgress = function (number, count, message, bPartTwo) {
        
        // Determine the percentage we are at.
        var percentage = parseInt(number / count * 100);

        // Divide the percentage by two as this is one of two parts.
        percentage /= 2;

        // If this is part two, add the initial 50%.
        if (bPartTwo) {
            percentage += 50;
        }

        // Update the progress bar.
        g_oProgressBar.updateProressBar(percentage, message);
    };

    /**
     * Confirms and starts the auto auto selection process.
     * Validates the necessary conditions and initiates the process based on the selected document and minimum words.
     */
    TextCompareFullDocumentSelection.prototype.ConfirmToStartFullDocumentSelection = function () {
        // Hide the auto auto options.
        g_oTextCompare.toggleTextCompareFullDocumentSelectionOptions();

        //// If text has not been loaded for one or more canvas, this can not be used.
		if (!g_oTextCompare.g_bArtworkTextDataLoaded || !g_oTextCompare.g_bCompareTextDataLoaded) {
			g_oGreenlightMessages.displayErrorBubble("One or more documents have no live text.");
			return;
		}

		// Make sure document Ids are different for a full document auto match.
		if (g_oViewer.g_oArtworkViewer.g_oDoc.documentId === g_oCompare.g_oCompareViewer.g_oDoc.documentId) {
			g_oGreenlightMessages.displayErrorBubble("Document auto matching can not be performed against the same document.");
			return false;
        }

        var iMatchFromDocument = parseInt($('#textComparison_FullDocumentSelection_selMatchFromDocument').val(), 10);
        var iMinWords = parseInt($('#textComparison_FullDocumentSelection_inputMinMatchWords').val(), 10);

        if (iMatchFromDocument === 0) {
            // Process the full document auto selection, searches through approval document for matching text on the compare.
            g_oTextCompare_FullDocumentSelection.processFullDocumentSelection(g_oCompare.g_oCompareViewer, g_oViewer.g_oArtworkViewer, false, iMinWords);
            
        }
		else {
            // Process the full document auto selection, searches through compare document for matching text on the approval.
            g_oTextCompare_FullDocumentSelection.processFullDocumentSelection(g_oViewer.g_oArtworkViewer, g_oCompare.g_oCompareViewer, true, iMinWords);
		}
    };

    // Add the text compare auto selection to the window object.
    window.TextCompareFullDocumentSelection = TextCompareFullDocumentSelection;
}());